import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, ConfirmDialog, DataGrid, StatusBadge, useToast, type DataGridColumn } from '@kss/ui';
import { Plus, RefreshCw, Trash2, Pencil } from 'lucide-react';
import { encodeId } from '../../utils/idCodec';
import { StandardAddDropdown } from '../shared/AddDropdown';
import type { Warehouse } from '@kss/api';
import { warehouseService } from '@kss/api';
import { useModulePerms } from '../../hooks/useModulePerms';

export default function AllWarehouses() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;
  const perms = useModulePerms('Warehouse');
  const [rows, setRows] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<Warehouse | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const data = await warehouseService.all(); setRows(Array.isArray(data) ? data : []); }
    catch (e) { const msg = e instanceof Error ? e.message : 'Failed to load'; setError(msg); toastRef.current.error('Load failed', msg); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete?.warehouseId) return;
    setDeleting(true);
    try { await warehouseService.delete(confirmDelete.warehouseId); toast.success('Deleted', `${confirmDelete.name} removed`); setConfirmDelete(null); await load(); }
    catch (e) { toast.error('Delete failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setDeleting(false); }
  };

  const columns: DataGridColumn<Warehouse>[] = [
    { accessor: 'code', title: 'Code', sortable: true, filterable: true, width: 120 },
    { accessor: 'name', title: 'Name', sortable: true, filterable: true },
    {
      // Same column-binding fix as AllStores — `address` doesn't
      // exist on the live Warehouse row, only `addressLine1` /
      // `city` / `state` / `postalCode`.
      accessor: 'addressLine1',
      title: 'Address',
      sortable: true,
      render: (r) => {
        const r2 = r as Warehouse & {
          addressLine1?: string;
          city?: string;
          state?: string;
          postalCode?: string;
        };
        const parts = [r2.addressLine1, r2.city, r2.state, r2.postalCode].filter(
          (s) => !!s && String(s).trim().length > 0,
        );
        return parts.length ? parts.join(', ') : '—';
      },
    },
    { accessor: 'isActive', title: 'Status', sortable: true, render: (r) => <StatusBadge status={r.isActive ? 'active' : 'inactive'} /> },
    { accessor: 'warehouseId', title: 'Actions', width: 110,
      render: (r) => (
        <div className="flex items-center gap-1">
          {perms.canUpdate && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover" onClick={(e) => { e.stopPropagation(); if (r.warehouseId) navigate(`/panel/warehouses/${encodeId(r.warehouseId)}`); }} title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
          )}
          {perms.canDelete && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover" onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }} title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
          )}
        </div>
      ) },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <DataGrid<Warehouse>
        title={<span className="hidden sm:inline">All warehouses</span>}
        columnPicker data={rows} columns={columns} selectable selectedIds={selected} onSelectedChange={setSelected}
        defaultPageSize={10} pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            <StandardAddDropdown basePath="/panel/warehouses" newLabel="New warehouse" service={warehouseService} templateFields={['name', 'code', 'addressLine1', 'city', 'state', 'postalCode', 'storeId', 'isActive']} module="Warehouse" />
          </div>
        }
      />
      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => (deleting ? undefined : setConfirmDelete(null))} onConfirm={handleDelete}
        title="Delete warehouse" message={confirmDelete ? `Remove "${confirmDelete.name}"?` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'} variant="danger" />
    </div>
  );
}
